import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as auth0 from 'auth0-js';
import { of, timer } from 'rxjs';

import { TenantService } from 'app/white-label/services/tenant.service';
import { environment } from 'environments/environment';
import { mergeMap } from 'rxjs/operators';


@Injectable()
export class AuthService {

	refreshSubscription: any;
	offset: any;
	auth0: any;

	constructor(
		public router: Router,
		private whiteLabelTenantService: TenantService
	) {
		this._idToken = '';
		this._accessToken = '';
		this._expiresAt = 0;

		const isWhiteLabel = !!this.whiteLabelTenantService.resolveSlug();
		const redirectUri = isWhiteLabel ? `${window.location.origin}/loggedIn` : environment.auth.callbackURL;

		this.auth0 = new auth0.WebAuth({
			clientID: environment.auth.clientID,
			domain: environment.auth.domain,
			responseType: 'token',
			audience: environment.auth.audience,
			redirectUri,
			scope: 'openid email offline_access'
		});
	}

	private _idToken: string;

	get idToken(): string {
		return this._idToken != '' ? this._idToken : localStorage.getItem('id_token');
	}

	private _accessToken: string;

	get accessToken(): string {
		return this._accessToken != '' ? this._accessToken : localStorage.getItem('access_token');
	}

	private _expiresAt: number;

	get expiresAt(): number {
		return this._expiresAt != 0 ? this._expiresAt : Number(localStorage.getItem('expires_at'));
	}

	public login(): void {
		this.auth0.authorize();
	}

	public logout(): void {
		// Remove tokens and expiry time
		this._accessToken = '';
		this._idToken = '';
		this._expiresAt = 0;

		localStorage.removeItem('access_token');
		localStorage.removeItem('id_token');
		localStorage.removeItem('expires_at');

		this.unscheduleRenewal();

		this.auth0.logout({
			returnTo: window.location.origin
		});
	}

	public handleAuthentication(): void {

		this.auth0.parseHash((err, authResult) => {
			if (authResult && authResult.accessToken && authResult.idToken) {
				window.location.hash = '';
				this.localLogin(authResult);
				// this.router.navigate(['/']);
				this._redirect();
			} else if (err) {
				this._clearRedirect();
				console.error(`Error authenticating: ${err.error}`);
				this.router.navigate(['/']);
			}
		});
	}

	public localLogin(authResult): void {
		// Set the time that the Access Token will expire at
		// let expiresAt = (authResult.expiresIn * 1000) + Date.now();

		// Set the time that the access token will expire at
		this.offset = 30 * 60 * 1000;
		// if (authResult.expiresIn) {
		//   this.offset = authResult.expiresIn * 1000 - (1 * 60 * 1000); //20min before expiration
		// } else {
		//   this.offset = 60 * 60 * 1000;
		// }
		//const expiresAt = JSON.stringify(new Date().getTime() + this.offset);
		const expiresAt = new Date().getTime() + this.offset;

		this._accessToken = authResult.accessToken;
		this._idToken = authResult.idToken;
		this._expiresAt = expiresAt;

		try {
			localStorage.setItem('id_token', authResult.idToken);
		} catch (error) {
			if (error) {
				this.cleanLocalStorage();
			}
		}
		localStorage.setItem('access_token', authResult.accessToken);
		// localStorage.setItem('id_token', authResult.idToken);
		localStorage.setItem('expires_at', expiresAt.toString());

		this.scheduleRenewal();
	}

	cleanLocalStorage() {
		for (var key in localStorage) {
			if (key.indexOf('com.auth0.auth.') >= 0) {
				localStorage.removeItem(key);
			}
		}
	}

	public renewTokens(): void {
		this.auth0.checkSession({}, (err, authResult) => {
			if (authResult && authResult.accessToken) {
				this.localLogin(authResult);
			} else if (err) {
				// alert(`Could not get a new token (${err.error}: ${err.error_description}).`);
				const arrivedWithToken = sessionStorage.getItem('arrivedWithToken');
				if (arrivedWithToken === 'true') return;
				this.logout();
			}
		});
	}

	public isAuthenticated(): boolean {
		// Check whether the current time is past the access token's expiry time
		return this.accessToken && Date.now() < this.expiresAt;
	}

	public scheduleRenewal() {
		if (!this.isAuthenticated()) {
			return;
		}
		this.unscheduleRenewal();

		const expiresAt = this.expiresAt;

		const expiresIn$ = of(expiresAt).pipe(
			mergeMap(
				expiresAt => {
					const now = Date.now();
					// Use timer to track delay until expiration
					// to run the refresh at the proper time

					const source = timer(Math.max(1, expiresAt - now));
					this.refreshSubscription = source.subscribe(() => {
						this.renewTokens();
					});
					return source;
					// return Observable.timer(Math.max(1, expiresAt - now));
				}
			)
		);

		this.refreshSubscription = expiresIn$.subscribe(
			() => {
				this.renewTokens();
				this.scheduleRenewal();
			}
		);
	}

	public unscheduleRenewal() {
		if (this.refreshSubscription) {
			this.refreshSubscription.unsubscribe();
		}
	}

	private buildTenantInfoParam(): string {
		const tenant = this.whiteLabelTenantService.tenant;

		if (!tenant) {
			return JSON.stringify(null);
		}

		return JSON.stringify({
			branding: {
				primaryColor: tenant.branding.primaryColor,
				secondaryColor: tenant.branding.secondaryColor
			},
			level: tenant.level,
			levelName: tenant.levelName
		});
	}

	public loginManually(email: string, password: string): Promise<string> {
		return new Promise<string>((resolve, reject) =>
			this.auth0.login({
				realm: 'Username-Password-Authentication',
				email,
				password,
				// Dados do tenant para enviar diretamente para o auth0 e customizar/manegar o MFA 
				tenantInfo: this.buildTenantInfoParam()
			}, (err, authResult) => {

				if (err) {
					if (err.error === 'access_denied') {
						resolve('ACCESS_DENIED');
					} else {
						resolve('UNKNOWN_ERROR');
					}
				} else if (authResult && authResult.accessToken && authResult.idToken) {
					this.localLogin(authResult);
					resolve('OK');
				}
			}));
	}

	private _redirect() {
		const redirect = decodeURI(localStorage.getItem('authRedirect'));
		const navArr = [redirect || '/'];

		this.router.navigate(navArr);
		// Redirection completed; clear redirect from storage
		this._clearRedirect();
	}

	private _clearRedirect() {
		// Remove redirect from localStorage
		localStorage.removeItem('authRedirect');
	}

}
